import { createAppSelector, createSelectorShallowEqual } from 'app-components/utils/core/reselect';
import { Collection, Map as ImMap, List, type Set, isImmutable } from 'immutable';
import orderBy from 'lodash/orderBy';
import { LruObjectCache, createCachedSelector } from 're-reselect';
import { createSelector } from 'reselect';
import { getId } from '../core/resourceStoreTypes';
import { getCurrentDateString } from '../date/dateSelectors';

import { getLength, reduce } from 'app-components/utils/core/immutableHelpers';
import { DateTime } from 'luxon';
import { Account } from '../accounts';
import { getAccountsById, getPaymentSrcsByAccountId, getRefundSrcsByAccountId } from '../accounts/accountsSelectors';
import { getExpenseCOAIds, getIncomeCOAIds } from '../categories/categoriesSelectors';
import { chartOfAccountsTypes } from '../chart-of-accounts';
import { transactionsTypes } from './index';
import {
  getResourcesById,
  getTransactionById,
  getTransactionItemGroupsByCustomerId,
  getTransactionItemsById,
  getTransactionsById,
  getTransactionsOfTypes,
  getTxnsByIdInArAccounts,
  getTxnsInArAccountsByBusinessByCustomer,
  txnTypesCashAndPlanned,
} from './transactionsSelectors';
import { AllocationProps, Credit, TransactionItem, TransactionRecord, TransactionSubType } from './transactionsTypes';
import { isInvoiceTxn, isSplitTxn, isTransferTxn, sumAllocations, sumUnpaidAmounts } from './transactionsUtils';

export type InvoiceBucket = 'unpaid' | 'drafts' | 'recurring' | 'paid';

// This returns all invoice type txns (txn type is INVOICE, subtype can be anything)
//
export const getInvoiceTypeTxns = createSelectorShallowEqual(getResourcesById, (transactions) =>
  transactions
    .valueSeq()
    .filter((txn) => txn.type === 'INVOICE')
    .toArray(),
);

// This returns all invoices (txn type is INVOICE and subtype is INVOICE)
//
export const getInvoices = createSelectorShallowEqual(getResourcesById, (transactions) =>
  transactions
    .valueSeq()
    .filter((txn) => txn.type === 'INVOICE' && txn.subtype === 'INVOICE')
    .toArray(),
);

// This selector is used to get all the credit transactions that are in the accounts receivable accounts. This includes
// payments, credit-memos, & bad-debt.
//
export const selectAccountsReceivableCredits = createSelectorShallowEqual([getTxnsByIdInArAccounts], (transactions) =>
  transactions
    .valueSeq()
    .filter((txn) => txn.amount < -0.005)
    .toArray(),
);

export const selectInvoicesLastPaidDate = createSelectorShallowEqual([selectAccountsReceivableCredits], (payments) => {
  const lastPaidDates: Record<string, string | undefined> = {}; // Will be undefined for invoices that do not have a payment
  for (const payment of payments) {
    if (payment.allocations) {
      // forEach because it could be a List
      payment.allocations.forEach((alloc) => {
        if (
          alloc.txnId &&
          payment.postedOn &&
          (!lastPaidDates[alloc.txnId] || (lastPaidDates[alloc.txnId] ?? '') < payment.postedOn)
        ) {
          lastPaidDates[alloc.txnId] = payment.postedOn;
        }
      });
    }
  }
  return lastPaidDates;
});

// We return an array because the same invoice can show up in multiple buckets
export const calculateInvoiceBucket = (
  invoice: transactionsTypes.TransactionProps,
  amountPaid: number,
): InvoiceBucket[] => {
  if (!invoice.sentOn) {
    return ['drafts'];
  }
  if (amountPaid === 0) {
    return ['unpaid'];
  }
  const outstandingBalance = Math.abs(invoice.amount) - amountPaid;
  if (outstandingBalance > 0) {
    return ['unpaid', 'paid'];
  }
  return ['paid'];
  // TODO: recurring
};

export const calculatePaidAmountForInvoice = (
  id: string,
  allocations: ReadonlyArray<transactionsTypes.AllocationProps>,
): number => allocations.filter((alloc) => alloc.txnId === id).reduce((sum, alloc) => (alloc.amount ?? 0) + sum, 0);

export const selectPaidAmountForInvoices = createSelectorShallowEqual([getInvoices], (invoices) =>
  Object.fromEntries(invoices.map((invoice) => [getId(invoice), sumAllocations(invoice)])),
);

export const selectPaidAmountForInvoicesTypes = createSelectorShallowEqual([getInvoiceTypeTxns], (invoices) =>
  Object.fromEntries(invoices.map((invoice) => [getId(invoice), sumAllocations(invoice)])),
);

export const getInvoicesBuckets = createAppSelector(
  [getInvoiceTypeTxns, selectPaidAmountForInvoices],
  (invoices, paidAmounts): Record<string, InvoiceBucket[]> =>
    Object.fromEntries(
      invoices.map((invoice) => [getId(invoice), calculateInvoiceBucket(invoice, paidAmounts[getId(invoice)])]),
    ),
);

export const getInvoiceIds = createSelectorShallowEqual(
  [getInvoices, getInvoicesBuckets, (_: unknown, filter: InvoiceBucket) => filter, selectInvoicesLastPaidDate],
  (invoices, statuses, filter, selectInvoicesLastPaidDate) => {
    const sortedInvoices = orderBy(
      invoices,
      (invoice) => {
        if (filter === 'unpaid' || filter === 'drafts') {
          return [invoice.dueOn, invoice.createdAt];
        }

        if (filter === 'paid') {
          const paidOn = selectInvoicesLastPaidDate[invoice.id || ''];
          return [paidOn, invoice.dueOn];
        }
        return 1;
      },
      ['desc', 'desc'],
    );
    return sortedInvoices.map(getId).filter((id) => statuses[id].includes(filter));
  },
);

export type InvoiceStatus = 'overdue' | 'partial' | 'sent' | 'paid' | 'draft';
export const calculateInvoiceStatus = (
  invoice: transactionsTypes.TransactionProps,
  amountPaid: number,
  today: string,
): InvoiceStatus => {
  const isPaidInFull = Math.abs(invoice.amount) <= amountPaid;
  const hasPartialPayment = amountPaid > 0 && !isPaidInFull;
  const isDraft = !invoice.sentOn;
  const isOverdue = invoice.dueOn && invoice.dueOn < today;

  if (isDraft && amountPaid === 0) {
    return 'draft';
  }
  if (isPaidInFull) {
    return 'paid';
  }
  if (isOverdue) {
    return 'overdue';
  }
  if (hasPartialPayment) {
    return 'partial';
  }
  return 'sent';
};

export const calculateStatusOrder = (status: InvoiceStatus) => {
  switch (status) {
    case 'overdue':
      return 1;
    case 'draft':
      return 2;
    case 'sent':
      return 3;
    case 'partial':
      return 4;
    case 'paid':
      return 5;
    // no default
  }
};

export const isInvoiceInDraft = (invoice: transactionsTypes.TransactionProps) => {
  const hasAllocations = invoice.allocations && getLength(invoice.allocations) > 0;
  return !invoice.sentOn && !hasAllocations;
};

export const selectInvoiceChipStatuses = createAppSelector(
  [getInvoiceTypeTxns, selectPaidAmountForInvoicesTypes, getCurrentDateString],
  (invoicesTypes, paidAmounts, today) =>
    Object.fromEntries(
      invoicesTypes.map((invoice) => [
        getId(invoice),
        calculateInvoiceStatus(invoice, paidAmounts[getId(invoice)], today),
      ]),
    ),
);

export const getInvoiceMixpanelData = createSelector(
  [getInvoiceTypeTxns, selectInvoiceChipStatuses],
  (invoices, invoicesByStatus) => {
    const total_invoices = invoices.length;
    const { total_unpaid_invoices, total_past_due_invoices, totalValue } = invoices.reduce(
      (acc, invoice) => {
        const status = invoicesByStatus[getId(invoice)];
        if (status === 'sent') {
          acc.total_unpaid_invoices++;
        } else if (status === 'overdue') {
          acc.total_past_due_invoices++;
        }
        acc.totalValue += invoice.amount;
        return acc;
      },
      { total_unpaid_invoices: 0, total_past_due_invoices: 0, totalValue: 0 },
    );
    const total_value_invoices = Math.round(totalValue * 100) / 100;
    return {
      total_invoices,
      total_unpaid_invoices,
      total_past_due_invoices,
      total_value_invoices,
    };
  },
);

export const getInvoiceStatusByInvoiceId = createAppSelector(
  [selectInvoiceChipStatuses, (_state: unknown, id: string) => id],
  (statuses, id) => statuses[id],
);

export const selectPaidAmountForProjectId = createAppSelector(
  [
    getInvoices,
    selectPaidAmountForInvoices,
    (_: unknown, props: { projectId: string; customerId: string }) => props.projectId,
    (_: unknown, props: { projectId: string; customerId: string }) => props.customerId,
  ],
  (invoices, paidAmounts, projectId, customerId) => {
    return invoices
      .filter(
        (invoice) =>
          invoice.projectId === projectId ||
          (!invoice.projectId && projectId === 'no-project' && invoice.customerId === customerId),
      )
      .reduce((sum, invoice) => sum + paidAmounts[getId(invoice)], 0);
  },
);

export const selectPaymentsForInvoiceId = createSelectorShallowEqual(
  [(state, id: string) => getTransactionById(state, id)?.allocations, selectAccountsReceivableCredits],
  (allocations, allPayments) => {
    if (!allocations) {
      return [];
    }

    const paymentsForId = allPayments.filter((paymentTxn) =>
      allocations.find((alloc) => alloc.txnId === paymentTxn.id),
    );
    paymentsForId.sort((a, b) => (a.postedOn < b.postedOn ? 1 : -1));
    return paymentsForId;
  },
);

export const selectAllAllocationsForId = createSelectorShallowEqual(
  [(state, id: string) => getTransactionById(state, id)?.allocations, getTransactionsById],
  (allocations, txns) => {
    if (!allocations) {
      return [];
    }

    const allocated = reduce(allocations, [] as TransactionRecord[], (acc, alloc) => {
      if (txns?.has(alloc.txnId)) {
        acc.push(txns?.get(alloc.txnId) as TransactionRecord);
      }
      return acc;
    });

    allocated.sort((a, b) => (a.postedOn < b.postedOn ? 1 : -1));
    return allocated;
  },
);

export const getCustomerCreditByBusinessId = createSelector(
  [selectAccountsReceivableCredits, (_: unknown, customerId: string) => customerId],
  (paymentTransactions, customerId) => {
    const bizToCredit = paymentTransactions.reduce<Record<string, number>>(
      (acc, payment) => {
        if (payment.customerId === customerId) {
          let creditAmount = Math.abs(payment.amount) ?? 0;
          if (payment.allocations) {
            for (const alloc of payment.allocations) {
              creditAmount -= alloc.amount ?? 0;
            }
          }
          acc.total += creditAmount;
          if (payment.businessId) {
            const currAmount = acc[payment.businessId] ?? 0;
            acc[payment.businessId] = currAmount + creditAmount;
          } else {
            // Payment missing biz id. This shouldn't happen.
            acc.other += creditAmount;
          }
        }
        return acc;
      },
      { total: 0, other: 0 },
    );
    return ImMap(bizToCredit);
  },
);

export const getProjectCreditByBusinessId = createSelector(
  [
    selectAccountsReceivableCredits,
    (_: unknown, customerId: string) => customerId,
    (_: unknown, _customerId: string, projectId: string | 'no-project') => projectId,
  ],
  (paymentTransactions, customerId, projectId) => {
    const bizToCredit = paymentTransactions.reduce<Record<string, number>>(
      (acc, payment) => {
        if (
          payment.customerId === customerId &&
          (payment.projectId === projectId || (projectId === 'no-project' && !payment.projectId))
        ) {
          let creditAmount = Math.abs(payment.amount) ?? 0;
          if (payment.allocations) {
            for (const alloc of payment.allocations) {
              creditAmount -= alloc.amount ?? 0;
            }
          }
          acc.total += creditAmount;
          if (payment.businessId) {
            const currAmount = acc[payment.businessId] ?? 0;
            acc[payment.businessId] = currAmount + creditAmount;
          } else {
            // Payment missing biz id. This shouldn't happen.
            acc.other += creditAmount;
          }
        }
        return acc;
      },
      { total: 0, other: 0 },
    );
    return ImMap(bizToCredit);
  },
);

export const isPayment = (txn: TransactionRecord) => txn.amount < -0.005 && isTransferTxn(txn);

export const getInvoiceCreditedAmount = (allocations?: AllocationProps[]) =>
  allocations?.reduce((acc, alloc) => {
    return alloc.type === 'CREDIT' ? acc + (alloc.amount ?? 0) : acc;
  }, 0) ?? 0;

export const getAvailableCreditTxns = createCachedSelector(
  (_: any, props: transactionsTypes.CreditTxnProps) => props.businessId,
  (_: any, props: transactionsTypes.CreditTxnProps) => props.customerId,
  (_: any, props: transactionsTypes.CreditTxnProps) => props.projectId,
  getTxnsInArAccountsByBusinessByCustomer,
  (businessId, customerId, projectId, txnsByBusinessByCustomer) => {
    // Get all transactions for the given business and customer
    let txns: Collection<string, transactionsTypes.TransactionRecord>;
    if (customerId) {
      if (businessId) {
        // Get all credits for business (and no-business) for customer (and no-customer)
        txns = Collection<string, transactionsTypes.TransactionRecord>().concat(
          txnsByBusinessByCustomer.get(businessId)?.get(customerId) ?? Collection(),
          txnsByBusinessByCustomer.get(businessId)?.get('no-customer') ?? Collection(),
          txnsByBusinessByCustomer.get('no-business')?.get(customerId) ?? Collection(),
          txnsByBusinessByCustomer.get('no-business')?.get('no-customer') ?? Collection(),
        ) as Collection<string, transactionsTypes.TransactionRecord>;
      } else {
        // Get all transactions for customer (and no-customer) across all businesses
        txns = txnsByBusinessByCustomer
          .valueSeq()
          .map((txnsByCustomer) =>
            Collection().concat(
              txnsByCustomer.get(customerId) ?? Collection(),
              txnsByCustomer.get('no-customer') ?? Collection(),
            ),
          )
          .flatten(true) as Collection<string, transactionsTypes.TransactionRecord>;
      }
    } else {
      if (businessId) {
        // Get all transactions for business (and no-business) across all customers
        txns = Collection<string, transactionsTypes.TransactionRecord>().concat(
          txnsByBusinessByCustomer.get(businessId)?.valueSeq().flatten(1) ?? Collection(),
          txnsByBusinessByCustomer.get('no-business')?.valueSeq().flatten(1) ?? Collection(),
        ) as Collection<string, transactionsTypes.TransactionRecord>;
      } else {
        // Get all transactions across all businesses and customers
        txns = txnsByBusinessByCustomer.flatten(2) as Collection<string, transactionsTypes.TransactionRecord>;
      }
    }

    // filter for specified project (if any) and only credit txns that are pending
    return txns.filter((txn) => {
      if (projectId) {
        if (projectId === 'no-project' ? txn.projectId : txn.projectId !== projectId) {
          return false;
        }
      }
      return txn.amount < -0.005 && txn.state === 'PENDING';
    });
  },
)({
  keySelector: (_state: any, props: { businessId?: string | null; customerId?: string | null }) =>
    `${props.businessId ?? 'unspecified-business'}${props.customerId ?? 'unspecified-customer'}`,
  cacheObject: new LruObjectCache({ cacheSize: 5 }),
});

export const getAvailableCredits = createSelector(
  [
    (_, props: transactionsTypes.AvailableCreditsProps) => props.action,
    (_, props: transactionsTypes.AvailableCreditsProps) => props.srcTxn,
    (_, props: transactionsTypes.AvailableCreditsProps) => props.businessId,
    (_, props: transactionsTypes.AvailableCreditsProps) => props.customerId,
    getAvailableCreditTxns,
  ],
  (action, srcTxn, businessId, customerId, txns): Credit[] => {
    if (
      (action !== 'ASSIGN_CREDITS' && action !== 'RECORD_PAYMENT' && (!srcTxn || !isPayment(srcTxn))) ||
      !businessId ||
      !customerId
    ) {
      return [];
    }

    return txns
      .valueSeq()
      .map((txn) => {
        const allocations = (
          List.isList(txn.allocations) ? txn.allocations.toArray() : txn.allocations
        ) as AllocationProps[];
        const allocated = allocations?.reduce((sum, allocation) => sum + Number(allocation.amount ?? 0), 0) ?? 0;
        return {
          txn,
          amount: Math.abs(txn.amount) - allocated,
        };
      })
      .filter((result: Credit) => result.amount > 0.005 && result.txn.id !== srcTxn?.id)
      .sort((a, b) => {
        // Sorting FIFO.
        return a.txn.postedOn > b.txn.postedOn ? 1 : -1;
      })
      .toArray();
  },
);

//** Updates invoice allocation field when virtual "credit" field is changed on invoice form. */
export const getUpdatedAllocations = createSelector(
  [
    (_s: unknown, props: transactionsTypes.UpdatedAllocationsProps) => props.creditedAmount,
    (_s: unknown, props: transactionsTypes.UpdatedAllocationsProps) => props.allocations,
    (_s: unknown, props: transactionsTypes.UpdatedAllocationsProps) => props.businessId,
    (_s: unknown, props: transactionsTypes.UpdatedAllocationsProps) => props.customerId,
    (state, props) => getAvailableCredits(state, { ...props, action: 'ASSIGN_CREDITS' }),
    getTxnsByIdInArAccounts,
  ],
  (creditedAmount, allocations, businessId, customerId, sortedCredits, txns): Array<AllocationProps> => {
    if (!allocations || !businessId || !customerId) return [];
    const newAllocations = (isImmutable(allocations) ? allocations.toJS() : [...allocations]) as AllocationProps[];

    const currentCreditAmount = getInvoiceCreditedAmount(newAllocations);
    // Keeps track of how many credits we have left to assign / unassign
    let creditsAvailable = creditedAmount - currentCreditAmount;

    // Assign credit allocations to the invoice
    if (creditsAvailable > 0) {
      // Sort by FIFO
      for (const credit of sortedCredits) {
        if (!credit.txn.id) continue; // should not happen.
        if (creditsAvailable <= 0) break;
        const alloc = newAllocations.find((alloc) => alloc.txnId === credit.txn.id && alloc.type === 'CREDIT');
        // Add to existing allocation or create a new one
        if (alloc) {
          alloc.amount += Math.min(creditsAvailable, credit.amount);
        } else {
          newAllocations.push({
            txnId: credit.txn.id,
            type: 'CREDIT',
            amount: Math.min(creditsAvailable, credit.amount),
          });
        }
        creditsAvailable -= credit.amount;
      }
      // Unassign credit allocations from invoice.
    } else if (creditsAvailable < 0) {
      // sort by LIFO
      newAllocations.sort((a, b) =>
        (txns.get(a.txnId)?.postedOn ?? '') > (txns.get(b.txnId)?.postedOn ?? '') ? -1 : 1,
      );
      for (const alloc of newAllocations) {
        if (creditsAvailable >= 0) break;
        if (alloc.type === 'CREDIT') {
          const amountToTake = Math.min(alloc.amount, Math.abs(creditsAvailable));
          alloc.amount -= amountToTake;
          creditsAvailable += amountToTake;
        }
      }
    }

    // remove allocations that are 0
    return newAllocations.filter((alloc) => alloc.amount !== 0);
  },
);

export const getAvailableCredit = createCachedSelector([getAvailableCreditTxns], (txns) => {
  const availableCredit = txns.valueSeq().reduce((credit, txn) => {
    const allocations = (txn.allocations ?? List()) as List<transactionsTypes.Allocation>;
    const allocated = allocations.reduce((sum, allocation) => sum + Number(allocation.amount ?? 0), 0.0);
    return credit + (Math.abs(txn.amount) - allocated);
  }, 0.0);
  return availableCredit;
})({
  keySelector: (_state: any, props: { businessId?: string | null; customerId?: string | null }) =>
    `${props.businessId ?? 'unspecified-business'}${props.customerId ?? 'unspecified-customer'}`,
  cacheObject: new LruObjectCache({ cacheSize: 5 }),
});

// Retrieve transactions that could be a refund to a customer. These would generally be transactions that are
// downloaded into an asset account (though user may have entered them manually) and has a negative amount
// along with compatible COA (e.g. income category, uncategorized income, or external transfer).
//
export const getRefundCompatibleTxns = createSelector(
  [getRefundSrcsByAccountId, (state) => getTransactionsOfTypes(state, txnTypesCashAndPlanned), getExpenseCOAIds],
  (accountsById, txns, expenseCoaIds) =>
    txns
      .filter(
        (txn) =>
          txn?.source !== 'SCHEDULED_TRANSACTION_PENDING' &&
          accountsById.has(txn.accountId ?? '') &&
          txn.amount < 0.005 &&
          txn.coa?.type !== 'ACCOUNT' &&
          (txn.coa?.type !== 'BALANCE_ADJUSTMENT' ||
            txn.coa?.id === chartOfAccountsTypes.BalanceAdjustmentTypeEnum.TRANSFER) &&
          !isSplitTxn(txn) &&
          !expenseCoaIds.includes(txn.coa?.id ?? ''),
      )
      .sortBy(
        ({ postedOn }) => postedOn,
        (a, b) => b.localeCompare(a),
      ),
);

export const isPaymentCompatibleItem = (
  transactionItem: TransactionItem,
  accountsById: ImMap<string, Account>,
  incomeCOAIds: Set<string>,
) => {
  const item = transactionItem.item;
  return (
    item &&
    item.source !== 'SCHEDULED_TRANSACTION_PENDING' &&
    accountsById.has(item.accountId ?? '') &&
    item.amount > 0.005 &&
    item.coa?.type !== 'ACCOUNT' &&
    (item.coa?.type !== 'BALANCE_ADJUSTMENT' ||
      item.coa?.id === chartOfAccountsTypes.BalanceAdjustmentTypeEnum.TRANSFER) &&
    incomeCOAIds.includes(item.coa?.id ?? '') &&
    item.id
  );
};

export const isRefundCompatibleItem = (
  transactionItem: TransactionItem,
  accountsById: ImMap<string, Account>,
  expenseCoaIds: Set<string>,
) => {
  const item = transactionItem.item;
  return (
    item &&
    item?.source !== 'SCHEDULED_TRANSACTION_PENDING' &&
    accountsById.has(item.accountId ?? '') &&
    item.amount < 0.005 &&
    item.coa?.type !== 'ACCOUNT' &&
    (item.coa?.type !== 'BALANCE_ADJUSTMENT' ||
      item.coa?.id === chartOfAccountsTypes.BalanceAdjustmentTypeEnum.TRANSFER) &&
    !expenseCoaIds.includes(item.coa?.id ?? '') &&
    item.id
  );
};

export const getPaymentCompatibleTxns = createSelector(
  [getPaymentSrcsByAccountId, (state) => getTransactionsOfTypes(state, txnTypesCashAndPlanned), getIncomeCOAIds],
  (accountsById, txns, incomeCOAIds) => {
    // TODO: Would be nice to support split transactions but requires the TransactionSelectList to be able to
    //       handle split transactions
    return txns
      .filter(
        (txn) =>
          txn?.source !== 'SCHEDULED_TRANSACTION_PENDING' &&
          accountsById.has(txn.accountId ?? '') &&
          txn.amount > 0.005 &&
          txn.coa?.type !== 'ACCOUNT' &&
          (txn.coa?.type !== 'BALANCE_ADJUSTMENT' ||
            txn.coa?.id === chartOfAccountsTypes.BalanceAdjustmentTypeEnum.TRANSFER) &&
          !isSplitTxn(txn) &&
          incomeCOAIds.includes(txn.coa?.id ?? ''),
      )
      .sortBy(
        ({ postedOn }) => postedOn,
        (a, b) => b.localeCompare(a),
      );
  },
);

export const getPaymentCompatibleTxnItemIds = createSelector(
  [getPaymentSrcsByAccountId, getTransactionItemsById, getIncomeCOAIds],
  (accountsById, txnItemsById, incomeCOAIds) => {
    return txnItemsById.reduce((acc: string[], txnItem: TransactionItem) => {
      const item = txnItem.item;
      if (isPaymentCompatibleItem(txnItem, accountsById, incomeCOAIds) && item.id) {
        acc.push(item.id);
      }
      return acc;
    }, []);
  },
);
export const getRefundCompatibleTxnItemIds = createSelector(
  [getRefundSrcsByAccountId, getTransactionItemsById, getExpenseCOAIds],
  (accountsById, txnItemsById, expenseCoaIds) => {
    return txnItemsById.reduce((acc: string[], txnItem: TransactionItem) => {
      const item = txnItem.item;
      if (isRefundCompatibleItem(txnItem, accountsById, expenseCoaIds) && item.id) {
        acc.push(item.id);
      }
      return acc;
    }, []);
  },
);

export const selectCreditAmountsForCustomerOrProject = createSelector([getAvailableCreditTxns], (credits) => {
  return credits.reduce(
    (sum: any, txn: any) => {
      const sumCopy = sum;
      const allocationSum = Math.abs(sumAllocations(txn)) || 0;

      if (allocationSum === 0 && !txn.sentOn && txn.subtype === TransactionSubType.InvoiceCreditMemo) {
        // draft credit memo
        sumCopy.draft += txn.amount;
      } else {
        sumCopy.total += txn.amount + allocationSum;
      }
      return sumCopy;
    },
    { draft: 0, total: 0 },
  );
});

export const selectOpenCreditsPerBusinessAndProject = createSelector([getAvailableCreditTxns], (credits) =>
  credits.reduce((result: any, txn: any) => {
    if (txn.businessId) {
      if (!result[txn.businessId]) {
        result[txn.businessId] = { total: 0, projects: {} };
      }
      const allocationSum = Math.abs(sumAllocations(txn)) || 0;
      const amount = (txn.amount || 0) + allocationSum;
      result[txn.businessId].total += amount;
      if (txn.projectId) {
        result[txn.businessId].projects[txn.projectId] = (result[txn.businessId].projects[txn.projectId] || 0) + amount;
      }
    }
    return result;
  }, {}),
);

export const selectInvoicesForCustomer = createSelectorShallowEqual(
  [getResourcesById, (_: unknown, customerId: string) => customerId],
  (transactions, customerId) =>
    transactions
      .valueSeq()
      .filter((txn) => isInvoiceTxn(txn) && txn.customerId === customerId)
      .toArray(),
);

export const selectInvoicesForProject = createSelectorShallowEqual(
  [getResourcesById, (_: unknown, projectId: string) => projectId],
  (transactions, projectId) =>
    transactions
      .valueSeq()
      .filter((txn) => isInvoiceTxn(txn) && txn.projectId === projectId)
      .toArray(),
);
interface UnpaidAmounts {
  sent: number;
  draft: number;
}
export const selectUnpaidAmountsForCustomer = createSelector(
  [selectInvoicesForCustomer],
  (invoices): UnpaidAmounts => sumUnpaidAmounts(invoices),
);

export const selectUnpaidAmountsForProject = createSelector(
  [selectInvoicesForProject],
  (invoices): UnpaidAmounts => sumUnpaidAmounts(invoices),
);

export const getInvoicesForPanel = createSelector(
  [
    getTxnsByIdInArAccounts,
    getCurrentDateString,
    selectInvoicesLastPaidDate,
    (_state, props) => props.projectId,
    (_state, props) => props.customerId,
  ],
  (txnsByIdInArAccounts, date, invoicePaidOn, projectId, customerId) => {
    return txnsByIdInArAccounts
      .valueSeq()
      .reduce((result: TransactionRecord[], txn: TransactionRecord) => {
        let projectCheck = true;
        if (projectId) {
          if (projectId !== 'no-project') {
            projectCheck = txn.projectId === projectId;
          } else {
            projectCheck = !txn.projectId;
          }
        }

        if (
          (customerId ? txn.customerId === customerId : true) &&
          projectCheck &&
          txn.type === 'INVOICE' &&
          txn.subtype === 'INVOICE'
        ) {
          result.push(txn);
        }

        return result;
      }, [])
      .sort((a, b) => {
        const statusA = calculateInvoiceStatus(a, sumAllocations(a), date);
        const statusB = calculateInvoiceStatus(b, sumAllocations(b), date);

        if (statusA === statusB) {
          if (statusA === 'paid') {
            return (invoicePaidOn[getId(a)] ?? '') < (invoicePaidOn[getId(b)] ?? '') ? 1 : -1;
          }
          return (a.dueOn ?? '') > (b.dueOn ?? '') ? 1 : -1;
        }
        return calculateStatusOrder(statusA) - calculateStatusOrder(statusB);
      });
  },
);

// add up all income transactions not in A/R
// add up all A/R transfer transactions
// iterate through all invoice line items
// if sales tax, determine percent of allocation
// determine sales tax amount
// subtract sales tax amount from total amount

// note - unallocated income counts in a/r
// note - flip sign based on account (unearned income account is negative for example)

// unearned income - transfers in and out of an unearned income account (subtype unearned income)

export const getTotalSalesTaxAmountPaid = createSelector(
  [
    getTxnsByIdInArAccounts,
    (_state, props) => props.customerId,
    (_state, props) => props.projectId,
    (_state, props) => props.startDate ?? '1970-01-01',
    (state, props) => props.endDate ?? getCurrentDateString(state),
  ],
  (txnsById, customerId, projectId, startDate, endDate) => {
    let salesTaxAmt = 0;

    txnsById?.forEach((txn) => {
      let projectCheck = true;
      if (projectId) {
        if (projectId !== 'no-project') {
          projectCheck = txn.projectId === projectId;
        } else {
          projectCheck = !txn.projectId;
        }
      }
      if (
        !isTransferTxn(txn) ||
        getLength(txn.allocations) === 0 ||
        txn.customerId !== customerId ||
        (isInvoiceTxn(txn) && isInvoiceInDraft(txn)) ||
        !projectCheck ||
        // TODO: filter by allocation date
        !(txn.postedOn >= startDate && txn.postedOn <= endDate)
      ) {
        return;
      }

      txn.allocations?.forEach((alloc) => {
        const invoice = txnsById.get(alloc.txnId);
        if (!invoice) return;

        const salesTaxAmount = invoice?.split?.items?.find((item) => item.type === 'INVOICE_TAX')?.amount ?? 0;
        const salesTaxContributionPercentage = salesTaxAmount / invoice.amount;
        if (salesTaxContributionPercentage > 0) {
          salesTaxAmt += alloc.amount * salesTaxContributionPercentage;
        }
      });
    });

    return salesTaxAmt;
  },
);

const projectCheck = (txn: TransactionItem, projectId?: string) => {
  let projectCheck = true;
  if (projectId) {
    if (projectId !== 'no-project') {
      projectCheck = txn.item.projectId === projectId;
    } else {
      projectCheck = !txn.item.projectId;
    }
  }
  return projectCheck;
};

// add up all income transactions not in A/R
// add up all A/R transfer transactions
// iterate through all invoice line items
// if sales tax, determine percent of allocation
// determine sales tax amount
// subtract sales tax amount from total amount

// note - unallocated income counts in a/r
// note - flip sign based on account (unearned income account is negative for example)

// unearned income - transfers in and out of an unearned income account (subtype unearned income)

export const selectPaidAmountForCustomerId = createSelector(
  [
    (state, props) => getTransactionItemGroupsByCustomerId(state).get(props.customerId ?? ''),
    (state, props) => getTotalSalesTaxAmountPaid(state, { customerId: props.customerId, projectId: props.projectId }),
    (state, props) =>
      getTotalSalesTaxAmountPaid(state, {
        customerId: props.customerId,
        projectId: props.projectId,
        startDate: DateTime.fromISO(getCurrentDateString(state)).startOf('year').toISODate(),
      }),
    getAccountsById,
    (_state, props) => props.accountingType,
    (_state, props) => props.projectId,
  ],
  (txns, salesTax, salesTaxThisYear, accountsById, accountingType = 'cash', projectId) => {
    const incomeTxns = txns?.get('INCOME')?.filter((txn) => projectCheck(txn, projectId));
    const transferTxns = txns?.get('TRANSFER')?.filter((txn) => projectCheck(txn, projectId));
    let postedOnObj = { id: '', postedOn: '1970-01-01', amount: 0 };
    let totalPaid = 0;
    let thisYearPaid = 0;
    const currentYear = new Date().getFullYear().toString();
    if (incomeTxns) {
      incomeTxns.forEach((txn) => {
        const { subtype, postedOn, amount, id, clientId } = txn.item;

        // we don't include invoices in this calculation for cash accounting
        const isCash = accountingType === 'cash' && subtype !== 'INVOICE';
        const isAccrual = accountingType === 'accrual';

        if (
          (isInvoiceTxn(txn.transaction) && isInvoiceInDraft(txn.transaction)) ||
          (isCash && txn.transaction.type === 'INVOICE')
        ) {
          return;
        }

        if (isCash || isAccrual) {
          if (postedOn > postedOnObj.postedOn) {
            postedOnObj = {
              id: id ?? clientId ?? '',
              postedOn,
              amount,
            };
          }
          if (postedOn.startsWith(currentYear)) {
            thisYearPaid += amount;
          }
          totalPaid += amount;
        }
      });
    }
    // Cash based accounting includes when the money is paid, not when the work is done
    // If we transfer to unearned income, we need to include that as income bc transfers remove the income category
    // If we transfer to A/R, the same applies.
    if (accountingType === 'cash' && transferTxns) {
      transferTxns.forEach((txn) => {
        const { postedOn, clientId, id, amount } = txn.item;
        const account = accountsById.get(txn.item?.accountId ?? '');
        const destAccount = accountsById.get(txn.item?.coa?.id ?? '');
        const isUnearned = account?.subType === 'UNEARNED_INCOME' && account?.type === 'OTHER_LIABILITY';
        const isAR = account?.type === 'ACCOUNTS_RECEIVABLE' && destAccount?.subType !== 'SALES_TAX';
        const negator = isUnearned || account?.type === 'ACCOUNTS_RECEIVABLE' ? -1 : 1;

        if (isAR || isUnearned) {
          const updatedAmt = amount * negator;
          // add up all transfer transactions to A/R or unearned income
          if (postedOn > postedOnObj.postedOn) {
            postedOnObj = {
              id: id ?? clientId ?? '',
              postedOn,
              amount: updatedAmt,
            };
          }
          if (postedOn.startsWith(currentYear)) {
            thisYearPaid += updatedAmt;
          }
          totalPaid += updatedAmt;
        }
      });
    }
    if (accountingType === 'cash') {
      // note - intentionally not including last payment in subtracting sales tax
      totalPaid -= salesTax;
      thisYearPaid -= salesTaxThisYear;
    }
    const lastPayment = postedOnObj.amount;
    return { totalPaid, lastPayment, thisYearPaid };
  },
);
